// Sênior não vê o Financeiro por padrão (espelha utils/menuPermissions.ts do backend)
const FINANCIAL_ACCESS_ROLES = ['admin', 'super_admin', 'financial_manager'];
const GENERAL_ACCESS_ROLES = ['admin', 'super_admin', 'senior'];
const FINANCIAL_NOTIFICATION_TYPES = [
  'finance_due',
  'payment_received',
  'payment_pending',
  'payment_overdue',
  'expense_approved',
  'expense_rejected',
  'financial_alert',
];
const PROCESS_NOTIFICATION_TYPES = [
  'schedule_reminder',
  'process_deadline',
  'process_movement',
  'task_assigned',
  'task_comment',
];

const toTimeValue = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const sortNotificationsByNewest = (notifications = []) => {
  return [...notifications].sort((a, b) => {
    const dateA = toTimeValue(a?.createdAt || a?.updatedAt || a?.date);
    const dateB = toTimeValue(b?.createdAt || b?.updatedAt || b?.date);
    return dateB - dateA;
  });
};

export const getNotificationCategory = (notification = {}) => {
  if (notification.category) {
    return notification.category;
  }

  const type = String(notification.type || '').toLowerCase();
  if (FINANCIAL_NOTIFICATION_TYPES.includes(type)) {
    return 'financial';
  }
  if (PROCESS_NOTIFICATION_TYPES.includes(type)) {
    return 'process';
  }
  return 'general';
};

/**
 * Verificar se um usuário pode acessar o módulo financeiro
 */
export const canAccessFinancial = (userRole) => {
 
  return FINANCIAL_ACCESS_ROLES.includes(userRole);
};

/**
 * Verificar se um usuário possui acesso geral ao sistema
 */
export const hasGeneralAccess = (userRole) => {
  return GENERAL_ACCESS_ROLES.includes(userRole);
};

/**
 * Verificar se um usuário pode deletar
 */
export const canDelete = (userRole) => {
  return hasGeneralAccess(userRole);
};

/**
 * Verificar se um usuário pode acessar auditoria
 */
export const canAccessAudit = (userRole) => {
  return [...GENERAL_ACCESS_ROLES, 'financial_manager'].includes(userRole);
};

/**
 * Obter as abas visíveis baseado no role
 */
export const getVisibleTabs = (userRole) => {
  const tabs = ['Processos Ativos', 'Concluídos', 'Agendas', 'Tarefas'];
  
  if (canAccessFinancial(userRole)) {
    tabs.push('Financeiro');
  }
  
  return tabs;
};

/**
 * Filtrar notificações baseado no role
 */
export const filterNotificationsByRole = (notifications, userRole) => {
  const sortedNotifications = sortNotificationsByNewest(notifications || []);

  // Admins e seniors têm acesso geral: ver todas as notificações
  if (hasGeneralAccess(userRole)) {
    return sortedNotifications;
  }

  // Usuários do financeiro (ex.: financial_manager) veem apenas notificações financeiras
  if (canAccessFinancial(userRole)) {
    return sortedNotifications.filter((notification) => getNotificationCategory(notification) === 'financial');
  }

  // Advogados associados e demais roles veem tudo exceto financeiras
  if (userRole === 'associated_lawyer') {
    return sortedNotifications.filter((notification) => getNotificationCategory(notification) !== 'financial');
  }

  return sortedNotifications.filter((notification) => getNotificationCategory(notification) !== 'financial');
};

/**
 * Obter o rôle do usuário (com fallback para admin booleano)
 */
export const getUserRoleValue = (user) => {
  if (user?.userRole) {
    return user.userRole;
  }
  if (user?.admin) {
    return 'admin';
  }
  // Menor privilégio por padrão — evita escalonamento acidental
  return 'associated_lawyer';
};

/**
 * Chaves de menu configuráveis por usuário (espelha
 * gestao-processo-backend/src/utils/menuPermissions.ts — qualquer mudança
 * aqui deve ser replicada lá). "settings" não entra aqui de propósito: é
 * sempre visível, todo usuário precisa poder gerenciar a própria conta.
 */
export const MENU_ITEM_KEYS = [
  'plataforma-admin',
  'dashboard',
  'active-processes',
  'schedule',
  'legal-kanban',
  'publications',
  'consulta-envolvido',
  'financeiro',
  'clients',
  'upload',
  'document-templates',
  'concluded-processes',
  'company',
  'auditoria',
];

export const MENU_ITEM_LABELS = {
  'plataforma-admin': 'Administração Global',
  'dashboard': 'Meu Painel',
  'active-processes': 'Processos',
  'schedule': 'Agenda',
  'legal-kanban': 'Atividades',
  'publications': 'Publicações',
  'consulta-envolvido': 'Busca por CPF, CNPJ, OAB ou nome',
  'financeiro': 'Financeiro',
  'clients': 'Contatos',
  'upload': 'Importar dados',
  'document-templates': 'Modelos',
  'concluded-processes': 'Processos concluídos',
  'company': 'Conta e assinatura',
  'auditoria': 'Auditoria',
};

const ALWAYS_VISIBLE_MENU_ITEMS = [
  'dashboard', 'active-processes', 'schedule', 'legal-kanban', 'publications',
  'clients', 'document-templates', 'concluded-processes', 'company',
];

/**
 * Teto de itens de menu que o cargo permite (o mesmo padrão de hoje).
 */
export const getRoleDefaultMenuItems = (userRole, isSuperAdmin) => {
  const items = new Set(ALWAYS_VISIBLE_MENU_ITEMS);
  if (isSuperAdmin) items.add('plataforma-admin');
  if (canAccessFinancial(userRole)) items.add('financeiro');
  if (canAccessAudit(userRole)) items.add('auditoria');
  if (hasGeneralAccess(userRole)) {
    items.add('upload');
    items.add('consulta-envolvido');
  }
  return MENU_ITEM_KEYS.filter((key) => items.has(key));
};

/**
 * Itens de menu que o usuário realmente vê: o backend já manda o conjunto
 * calculado (`effectiveMenuItems`, que nunca amplia o teto do cargo — só
 * pode restringir). Se o cache local ainda não tiver esse campo (sessão
 * antiga, antes desta funcionalidade existir), cai para o padrão do cargo,
 * que é exatamente o comportamento de antes.
 */
export const getEffectiveMenuItems = (userData) => {
  if (Array.isArray(userData?.effectiveMenuItems)) {
    return userData.effectiveMenuItems;
  }
  const role = getUserRoleValue(userData);
  const isSuperAdmin = Boolean(userData?.super_admin || role === 'super_admin');
  return getRoleDefaultMenuItems(role, isSuperAdmin);
};
